'use es6';

import ReactDOMServer from 'react-dom/server';
import { HEFFALUMP, TRANSPARENT } from 'reporting-visualizations/constants/hubStyleTokensColors';
import { Chevron } from 'foundations-components/transitional/components/Chevron';
import { record as XYVisualizationRecord, getHighchartsOptions as getXYHighchartsOptions } from 'reporting-visualizations/visualizations/xy/xyVisualization';
import Highchart from 'reporting-visualizations/highcharts/Highchart';
import { fromJS } from 'immutable';
import { DEFAULT_DEBUG } from 'reporting-visualizations/constants/debug';
import { getReferenceLabel, getProperty } from 'reporting-data/v2/dataset/datasetRecords';
import { formatPercent, formatNumber } from 'reporting-visualizations/formatters/propertyFormatter';
import I18n from 'I18n';

/*
This logic is originally from https://git.hubteam.com/HubSpot/reporting/blob/d9b5d910aa3c1ead0013a5010ef2ab652fc77a04/reporting-ui-components/static/js/reports/aggregation/series/conversion-series.js

This creates a highcharts series with a scale of 0 to 100 so that UIChevrons can then be rendered
with the conversion %s displayed vertically aligned in the middle of the chart between each column.
*/
import { jsx as _jsx } from "react/jsx-runtime";
const getSeries = (dataset, {
  funnelValueProperty,
  funnelConversionProperty,
  conversionDataInRow
}) => {
  const data = dataset.get('data');
  const seriesData = {
    id: 'conversion-axis',
    name: 'conversion-axis-series',
    data: data ? data.map(row => {
      return row.get(funnelValueProperty) ? 50 : null;
    }) : null,
    index: -100,
    stacking: null,
    pointPlacement: -0.5,
    enableMouseTracking: false,
    showInLegend: false,
    dataLabels: {
      align: 'center',
      verticalAlign: 'middle',
      enabled: true,
      formatter() {
        const index = this.point.index;
        if (index === 0) return null;
        const datum = data.get(conversionDataInRow ? index : index - 1);
        if (datum.y === 0) return null;
        const label = getReferenceLabel(dataset, funnelConversionProperty, datum.get(funnelConversionProperty));
        return ReactDOMServer.renderToString( /*#__PURE__*/_jsx(Chevron, {
          children: label
        }));
      },
      useHTML: true,
      color: HEFFALUMP,
      y: 0,
      x: 0
    },
    type: 'line',
    xAxis: 'bottom',
    yAxis: 'conversion-axis',
    color: TRANSPARENT
  };
  return seriesData;
};
const conversionAxis = {
  id: 'conversion-axis',
  opposite: true,
  min: 0,
  max: 100,
  gridLineWidth: 0,
  title: {
    text: null
  },
  labels: {
    enabled: false
  },
  tickLength: 0
};
const addCumulativeArrowSeries = (dataset, {
  funnelValueProperty,
  funnelConversionProperty,
  conversionDataInRow
}) => ({
  axis: conversionAxis,
  series: getSeries(dataset, {
    funnelValueProperty,
    funnelConversionProperty,
    conversionDataInRow
  })
});
const noop = () => {};
export const getHighchartsOptions = visualization => {
  const datasetKey = visualization.get('dataset');
  const dataset = visualization.getIn(['data', datasetKey]);
  const tooltipFormatter = visualization.getIn(['style', 'tooltipFormatter']);
  const funnelFilterProperty = visualization.get('filterProperty');
  const funnelValueProperty = visualization.get('valueProperty');
  const xyViz = XYVisualizationRecord({
    data: visualization.get('data'),
    layers: fromJS([{
      dataset: datasetKey,
      x: {
        blend: 'bottom',
        field: funnelFilterProperty,
        type: 'category'
      },
      y: {
        blend: 'left',
        field: funnelValueProperty,
        type: 'linear'
      }
    }]),
    events: {
      onPointClick: visualization.events && visualization.events.onPointClick ? visualization.events.onPointClick : noop
    },
    style: {
      tooltipFormatter,
      dataLabels: true,
      y: {
        left: {
          title: ''
        }
      },
      disableZoom: visualization.get('disableZoom')
    }
  });
  const highchartsOptions = getXYHighchartsOptions(xyViz);
  const funnelConversionProperty = visualization.get('primaryChevronProperty');
  if (getProperty(dataset, funnelConversionProperty)) {
    const cumlativeSeries = addCumulativeArrowSeries(dataset, {
      funnelValueProperty,
      funnelConversionProperty,
      conversionDataInRow: visualization.get('conversionDataInRow')
    });
    return highchartsOptions.update('series', series => series.push(fromJS(cumlativeSeries.series))).update('yAxis', axis => {
      return axis.push(fromJS(cumlativeSeries.axis));
    }).set('accessibility', {
      point: {
        descriptionFormatter: point => {
          if (point.isNull) {
            return false;
          }
          const nextPoint = point.index < point.series.data.length - 1 ? point.series.data[point.index + 1] : undefined;
          const navigationPositionText = point.series.data.length === 1 ? I18n.text('reporting-visualizations.column-funnel.only-column') : point.index === 0 ? I18n.text('reporting-visualizations.column-funnel.first-column') : point.index === point.series.data.length - 1 ? I18n.text('reporting-visualizations.column-funnel.last-column') : '';
          const nextPointConversionText = nextPoint ? I18n.text('reporting-visualizations.column-funnel.column-change', {
            funnelPercent: formatPercent(nextPoint.y / point.y),
            nextColumnTitle: nextPoint.category
          }) : '';
          const pointValue = formatNumber(point.y, {
            shouldFormat: true
          });
          return `${point.category}, ${pointValue}. ${nextPointConversionText} ${navigationPositionText}`;
        }
      }
    });
  }
  return highchartsOptions;
};
export const BuildColumnFunnelVisualization = (visualization, debug = DEFAULT_DEBUG, onReportVisualize) => {
  const combinedChartOptions = getHighchartsOptions(visualization);
  debug.set('Highcharts', combinedChartOptions);
  return /*#__PURE__*/_jsx(Highchart, {
    chartOptions: combinedChartOptions,
    onReportVisualize: onReportVisualize
  });
};